/**
 * Tabela zamowien z GET /sfd-zamowienia: Sklep · Nr zamówienia · Data · Oczekiwana · Status · Dostawa ·
 * Netto · Brutto · Opis (skrocony, pelny w tooltipie). Klik w wiersz rozwija pozycje (PozycjeZamowienia).
 * Kontener ma overflow: auto - tabela nie rozpycha strony.
 */
import React, { useRef, useState } from 'react';
import PozycjeZamowienia from './PozycjeZamowienia';
import { formatujDate, formatujKwote, kolorStatusu, kolorPlatnosci, skroc, NIEZNANY_ADRES } from './format';

const KOLUMNY = ['Sklep', 'Nr zamówienia', 'Data', 'Oczekiwana', 'Status', 'Dostawa', 'Netto', 'Brutto', 'Opis'];
const th = (tekst, extra = {}) => (
  <th key={tekst} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', ...extra }}>{tekst}</th>
);
const td = { padding: '5px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'top', fontSize: '11px' };

const Znacznik = ({ kod, etykieta, kolory, testId }) => {
  const k = kolory(kod);
  return (
    <span data-testid={testId} title={kod === null || kod === undefined ? undefined : `kod z portalu: ${kod}`}
      style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', color: k.kolor, backgroundColor: k.tlo }}>
      {etykieta || '—'}
    </span>
  );
};

export const ZnacznikStatusu = ({ status }) => <Znacznik kod={status?.kod} etykieta={status?.etykieta} kolory={kolorStatusu} testId="status" />;
export const ZnacznikPlatnosci = ({ status }) => <Znacznik kod={status?.kod} etykieta={status?.etykieta} kolory={kolorPlatnosci} testId="status-platnosci" />;

/** Props: wiersze, ladowanie. Rozwiniecie: klik w wiersz (Enter/spacja z klawiatury). */
const Tabela = ({ wiersze, ladowanie }) => {
  const lista = Array.isArray(wiersze) ? wiersze : [];
  const [rozwiniete, setRozwiniete] = useState(() => new Set());
  const cache = useRef(new Map());

  const przelacz = (id) => {
    setRozwiniete(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <div data-testid="tabela-zamowien" style={{ overflow: 'auto', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 1 }}>
          <tr>
            {KOLUMNY.map(k => th(k, (k === 'Netto' || k === 'Brutto') ? { textAlign: 'right' } : {}))}
          </tr>
        </thead>
        <tbody>
          {lista.map((w, idx) => {
            const otwarty = rozwiniete.has(w.id);
            const nieznany = !w.sklep?.nazwa;
            return (
              <React.Fragment key={`${w.id}-${idx}`}>
                <tr data-testid="wiersz-zamowienia" role="button" tabIndex={0} aria-expanded={otwarty}
                  title={otwarty ? 'Kliknij, żeby zwinąć pozycje' : 'Kliknij, żeby rozwinąć pozycje'}
                  onClick={() => przelacz(w.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); przelacz(w.id); } }}
                  style={{ cursor: 'pointer', backgroundColor: otwarty ? 'var(--primary-light)' : (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)') }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600, color: nieznany ? 'var(--warning)' : 'var(--text)' }} title={w.adres?.tekst || undefined}>
                    {w.sklep?.nazwa || NIEZNANY_ADRES}
                    {w.adres?.miasto && <span style={{ display: 'block', fontSize: '10px', fontWeight: 400, color: 'var(--text-muted)' }}>{w.adres.miasto}</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                    <span style={{ marginRight: '4px', color: 'var(--text-muted)' }}>{otwarty ? '▾' : '▸'}</span>{w.numer}
                    {w.numer_obcy && <span style={{ display: 'block', fontSize: '10px', fontWeight: 400, color: 'var(--text-muted)' }}>obcy: {w.numer_obcy}</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatujDate(w.data)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatujDate(w.data_oczekiwana)}</td>
                  <td style={td}>
                    <ZnacznikStatusu status={w.status} />
                    {w.status_platnosci?.etykieta && <span style={{ display: 'block', marginTop: '2px' }}><ZnacznikPlatnosci status={w.status_platnosci} /></span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{w.dostawa || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatujKwote(w.netto)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600, color: 'var(--orange)' }}>{formatujKwote(w.brutto)}</td>
                  <td style={{ ...td, maxWidth: '260px' }} title={w.opis || undefined} data-testid="opis">{w.opis ? skroc(w.opis, 48) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                </tr>
                {otwarty && (
                  <tr data-testid="wiersz-pozycje">
                    <td colSpan={KOLUMNY.length} style={{ padding: 0, borderBottom: '1px solid var(--border)' }}>
                      <PozycjeZamowienia id={w.id} cache={cache.current} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {!ladowanie && lista.length === 0 && (
        <div data-testid="pusta-lista" style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
          Brak zamówień dla wybranych filtrów
        </div>
      )}
      {ladowanie && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie…</div>}
    </div>
  );
};

export default Tabela;
